import { useQueryClient } from '@tanstack/react-query';
import pluralize from 'pluralize';
import { type ChangeEvent, type MouseEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';

import { type UseTrackCanvasViewedParams, useTrackCanvasMediaImported } from '@air/analytics';
import { type Clip, ClipType } from '@air/api/types';
import { CANVAS_IMAGE_NODE_SPACING, commands, useCommandHistory } from '@air/feature-edit-canvas';
import { Banner } from '@air/primitive-banner';
import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';
import { convertUnknownToError, reportErrorToBugsnag } from '@air/utils-error';
import { cn } from '@air/utils-tailwind';

import {
  type CanvasRowItem,
  computeRowStart,
  getOpenCanvasRowItems,
} from '~/components/Canvas/utils/canvasRowPlacement';
import {
  type AddToCanvasLayout,
  CANVAS_CLIP_GRID_COLUMNS,
  clipsToAppNodes,
  getClipCanvasPlacements,
} from '~/components/Modals/AddToCanvasModal/clipsToAppNodes';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { useSelectedCanvasId } from '~/hooks/canvases/useSelectedCanvasId';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type CanvasDetail } from '~/swr-hooks/canvases/types';
import { getCanvasDetailKey, useFetchCanvasDetail } from '~/swr-hooks/canvases/useCanvas';
import { useListCanvases } from '~/swr-hooks/canvases/useListCanvases';
import { usePrivateCanvasesCreateNodesMutation } from '~/swr-hooks/canvases/usePrivateCanvasesCreateNodesMutation';

/** Maps a (not currently open) canvas's persisted nodes to row items so a new row can be appended beside them. */
const canvasNodesToRowItems = (canvas: CanvasDetail | undefined): CanvasRowItem[] => {
  return (canvas?.nodes ?? [])
    .filter((node) => !node.inactive && node.data.type !== 'group')
    .map((node) => ({ width: node.width, x: node.position.x, y: node.position.y }));
};

export type AddToCanvasModalProps = {
  clips: Clip[];
  /** `wrap` places a large set (a whole board) on a grid. The default is one row. */
  layout?: AddToCanvasLayout;
  onNewCanvas?: () => void;
  /** Assets dropped before `clips` because a canvas cannot accept them. */
  skippedCount?: number;
  /** Workspace the clips were loaded from. A later switch closes the modal instead of writing into the new workspace. */
  sourceWorkspaceId?: string;
  /** Attribution for the canvas_viewed event when the user picks an existing canvas */
  viewedFrom?: UseTrackCanvasViewedParams['viewed_from'];
};

const getLayoutColumns = (layout: AddToCanvasLayout | undefined) =>
  layout === 'wrap' ? CANVAS_CLIP_GRID_COLUMNS : undefined;

export const AddToCanvasModal = memo(
  ({
    clips,
    layout,
    onClose,
    onNewCanvas,
    skippedCount,
    sourceWorkspaceId,
    viewedFrom,
  }: AirModalProps<AddToCanvasModalProps>) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const queryClient = useQueryClient();
    const fetchCanvasDetail = useFetchCanvasDetail();
    const { showToast } = useToasts();
    const { mutateAsync: createNodes, isPending } = usePrivateCanvasesCreateNodesMutation();
    const { selectCanvas } = useSelectedCanvasId();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCanvasId, setSelectedCanvasId] = useState<string | null>(null);
    const { trackCanvasMediaImported } = useTrackCanvasMediaImported();
    const execute = useCommandHistory((state) => state.execute);

    const { data: canvases, isLoading } = useListCanvases({
      workspaceId: currentWorkspace?.id,
      view: 'mine',
    });

    const filteredCanvases = useMemo(() => {
      if (!canvases) {
        return [];
      }
      if (!searchQuery) {
        return canvases;
      }
      const lowerQuery = searchQuery.toLowerCase();

      return canvases.filter((canvas) => canvas.title?.toLowerCase().includes(lowerQuery));
    }, [canvases, searchQuery]);

    const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
    }, []);

    const handleCanvasSelect = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      const canvasId = event.currentTarget.dataset.canvasId;

      if (!canvasId) {
        return;
      }

      setSelectedCanvasId((prev) => (prev === canvasId ? null : canvasId));
    }, []);

    const workspaceChanged = Boolean(
      sourceWorkspaceId && currentWorkspace?.id && currentWorkspace.id !== sourceWorkspaceId,
    );

    useEffect(() => {
      if (workspaceChanged) {
        onClose();
      }
    }, [onClose, workspaceChanged]);

    const handleNewCanvas = useCallback(() => {
      if (sourceWorkspaceId && currentWorkspace?.id !== sourceWorkspaceId) {
        onClose();
        return;
      }

      onClose();
      onNewCanvas?.();
    }, [currentWorkspace?.id, onClose, onNewCanvas, sourceWorkspaceId]);

    const handleSubmit = useCallback(async () => {
      if (sourceWorkspaceId && currentWorkspace?.id !== sourceWorkspaceId) {
        onClose();
        return;
      }

      const targetWorkspaceId = sourceWorkspaceId ?? currentWorkspace?.id;

      if (!selectedCanvasId || !targetWorkspaceId) {
        return;
      }

      try {
        const rightPanelState = useWorkspaceRightPanelStore.getState();
        const isTargetCanvasOpen = rightPanelState.open && rightPanelState.selectedCanvasId === selectedCanvasId;

        if (isTargetCanvasOpen) {
          // Canvas is already open — add directly to the live store so the nodes appear immediately.
          // addNodes also triggers server sync via the injected createNodes callback. Append the new
          // row beside the existing content so it doesn't land on top of (and hide) assets at (0, 0).
          const { startX, startY } = computeRowStart(getOpenCanvasRowItems());
          execute(
            commands.addNodes(
              clipsToAppNodes(clips, {
                columns: getLayoutColumns(layout),
                spacing: CANVAS_IMAGE_NODE_SPACING,
                startX,
                startY,
              }),
              {
                zoomToFit: false,
                zoomToFitIfFirstNode: true,
              },
            ),
          );
        } else {
          // Canvas is not open — read its existing nodes so the new row appends beside them
          // (matching the open-canvas behavior), then persist via API. Afterwards remove the
          // cached canvas detail so the fresh fetch triggered by selectCanvas isn't beaten by
          // stale cached data. (useCanvasForRightPanel only syncs nodes once per canvas open; if
          // stale cache is used for that first sync, the new nodes would be silently dropped.)
          // Reading the existing nodes is best-effort: on failure we lay out from the origin
          // rather than blocking the add.
          let persistedRowItems: CanvasRowItem[] = [];
          try {
            const canvas = await fetchCanvasDetail({ workspaceId: targetWorkspaceId, canvasId: selectedCanvasId });
            persistedRowItems = canvasNodesToRowItems(canvas);
          } catch {
            persistedRowItems = [];
          }
          const { startX, startY } = computeRowStart(persistedRowItems);
          const placements = getClipCanvasPlacements(clips, {
            columns: getLayoutColumns(layout),
            spacing: CANVAS_IMAGE_NODE_SPACING,
            startX,
            startY,
          });
          await createNodes({
            workspaceId: targetWorkspaceId,
            canvasId: selectedCanvasId,
            nodes: clips.flatMap((clip, index) => {
              const placement = placements[index];

              if (!placement) {
                return [];
              }

              return [
                {
                  name: clip.title || clip.importedName || '',
                  position: { x: placement.x, y: placement.y },
                  width: placement.width,
                  height: placement.height,
                  zIndex: '1',
                  data: {
                    type: clip.type === ClipType.video ? 'video' : 'image',
                    fileName: clip.title || clip.importedName || '',
                    media: {
                      image: clip.assets?.image,
                      original: clip.assets?.original,
                      ...(clip.type === ClipType.video && { video: clip.assets?.original }),
                    },
                    source: 'workspace',
                    assetId: clip.assetId,
                    versionId: clip.id,
                  },
                },
              ];
            }),
          });

          queryClient.removeQueries({
            queryKey: getCanvasDetailKey({ canvasId: selectedCanvasId, workspaceId: targetWorkspaceId }),
          });
          selectCanvas(selectedCanvasId, {
            viewedFrom,
          });
        }

        trackCanvasMediaImported({
          canvas_id: selectedCanvasId,
          number_of_media: clips.length,
          source: 'workspace',
        });

        const label = clips.length === 1 ? 'Asset added to Canvas' : `${clips.length} assets added to Canvas`;
        showToast(label, { color: 'black' });
        onClose();
      } catch (error) {
        showToast('Failed to add assets to Canvas', { color: 'red' });
        reportErrorToBugsnag({
          error: convertUnknownToError(error),
          context: 'Failed to add assets to canvas',
        });
      }
    }, [
      selectedCanvasId,
      currentWorkspace?.id,
      sourceWorkspaceId,
      queryClient,
      fetchCanvasDetail,
      createNodes,
      selectCanvas,
      clips,
      layout,
      showToast,
      onClose,
      trackCanvasMediaImported,
      execute,
      viewedFrom,
    ]);

    return (
      <Modal isOpen onDismiss={onClose}>
        <div className="flex flex-col gap-4">
          <header className="flex justify-between gap-4">
            <ModalTitle data-testid="ADD_TO_CANVAS_MODAL_TITLE">Add to Canvas...</ModalTitle>
            <ModalCloseButton className="shrink-0" onClick={onClose} />
          </header>
          {!!skippedCount && (
            <Banner appearance="warning" data-testid="ADD_TO_CANVAS_MODAL_SKIPPED_BANNER" size="small">
              {pluralize('asset', skippedCount, true)} can&apos;t be added to a Canvas.
            </Banner>
          )}
          <p className="ads:text-metadata text-14 text-gray-10">
            {`The ${pluralize('asset', clips.length, clips.length > 1)}`} will be added to a Canvas and will not be
            removed from their current location.
          </p>
          <Input
            onChange={handleSearchChange}
            placeholder={`Search ${currentWorkspace?.name ?? 'Canvases'}`}
            value={searchQuery}
          />
          <div className="overflow-hidden rounded-8 border border-gray-5 p-3">
            <p className="p-2 font-medium ads:text-secondary text-14 text-gray-11">My Canvases</p>
            <div className="max-h-60 overflow-y-auto">
              {isLoading && <p className="py-2 ads:text-metadata text-14 text-gray-10">Loading Canvases...</p>}
              {!isLoading && filteredCanvases.length === 0 && (
                <p className="py-2 ads:text-metadata text-14 text-gray-10">No Canvases found</p>
              )}
              {filteredCanvases.map((canvas) => (
                <button
                  className={cn(
                    'w-full truncate py-2 pl-6 text-left ads:text-primary text-14 text-gray-12',
                    selectedCanvasId === canvas.id
                      ? 'ads:bg-primary-subtle bg-blue-3'
                      : 'hover:ads:bg-subtle hover:bg-gray-3',
                  )}
                  data-canvas-id={canvas.id}
                  data-testid="ADD_TO_CANVAS_MODAL_CANVAS_ITEM"
                  key={canvas.id}
                  onClick={handleCanvasSelect}
                  type="button"
                >
                  {canvas.title}
                </button>
              ))}
            </div>
          </div>
          <footer className={cn('flex', onNewCanvas ? 'justify-between' : 'justify-end')}>
            {onNewCanvas && (
              <Button
                appearance="outline"
                color="gray"
                data-testid="ADD_TO_CANVAS_MODAL_NEW_CANVAS_BUTTON"
                onClick={handleNewCanvas}
                size="large"
              >
                New Canvas
              </Button>
            )}
            <div className="flex gap-3">
              <Button
                appearance="ghost"
                color="gray"
                data-testid="ADD_TO_CANVAS_MODAL_CANCEL_BUTTON"
                onClick={onClose}
                size="large"
              >
                Cancel
              </Button>
              <Button
                appearance="filled"
                color="blue"
                data-testid="ADD_TO_CANVAS_MODAL_SUBMIT_BUTTON"
                disabled={!selectedCanvasId || isPending}
                isLoading={isPending}
                onClick={handleSubmit}
                size="large"
              >
                {isPending ? 'Adding...' : 'Add to Canvas'}
              </Button>
            </div>
          </footer>
        </div>
      </Modal>
    );
  },
);

AddToCanvasModal.displayName = 'AddToCanvasModal';
